import { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { FiX } from "react-icons/fi";
import { updatePaymentGroup } from "../../../../../../services/paymentGroupService";
import { StepHeader, Panel, Empty, Notice, CountPill } from "../primitives";

// Mirrors NEVER_MAPPABLE_TYPES in app/services/schedule/derive.js. The name
// list is the same belt-and-braces check the Activities Details screen uses,
// for groups whose type column is not one of the known constants.
const NEVER_MAPPABLE_TYPES = new Set([
  "PROJECT_INITIAL_DEPOSIT",
  "DESIGN_INITIAL_DEPOSIT",
  "PROJECT_WARRANTY_COMPLETION",
]);
const NEVER_MAPPABLE_NAMES = ["initial deposit", "warranty"];

/**
 * Move an activity between the unmapped pool and a milestone, in a schedule
 * snapshot. Pure — the same rewrite is applied to both the React state and
 * the ref that mirrors it (see moveInCache below).
 */
const applyMove = (old, activityId, toGroupId, fromGroupId) => {
  if (!old) return old;

  return {
    ...old,
    billing_milestones: (old.billing_milestones || []).map((group) => {
      let ids = group.activity_ids || [];
      if (fromGroupId && group.group_id === fromGroupId) {
        ids = ids.filter((id) => id !== activityId);
      }
      if (toGroupId && group.group_id === toGroupId && !ids.includes(activityId)) {
        ids = [...ids, activityId];
      }
      return ids === group.activity_ids ? group : { ...group, activity_ids: ids };
    }),
    activities: (old.activities || []).map((activity) => {
      if (activity.id !== activityId) return activity;
      let links = activity.billing_milestone_ids || [];
      if (fromGroupId) links = links.filter((id) => id !== fromGroupId);
      if (toGroupId && !links.includes(toGroupId)) links = [...links, toGroupId];
      return { ...activity, billing_milestone_ids: links };
    }),
  };
};

/**
 * Step 2 — drag activities onto billing milestones.
 *
 * Writes go through the same middleware endpoint the Activities Details screen
 * uses (PUT project/payment-group), so there is one implementation of the
 * mapping. Two things keep dragging instant:
 *
 *   1. the schedule state is updated optimistically, so the card moves on
 *      drop instead of after a round-trip plus a full refetch;
 *   2. writes are queued on a promise chain, because the endpoint replaces the
 *      whole link list for a group — two in-flight writes for the same group
 *      could otherwise land out of order and drop a link.
 *
 * Standard milestones (Initial Deposit / Handover / Warranty) are not drop
 * targets: their dates come from the project window, so a mapping would have
 * no effect.
 */
const MappingStep = ({ schedule, editable, setSchedule, refetchSchedule }) => {
  const [draggingId, setDraggingId] = useState(null);
  const [hoverGroupId, setHoverGroupId] = useState(null);
  const [pendingWrites, setPendingWrites] = useState(0);
  const queueRef = useRef(Promise.resolve());

  // The payload for a queued write is built from the schedule as it is at
  // send time (portal read it from the react-query cache). A ref mirrors the
  // state so an in-flight chain sees drops that landed after it was queued,
  // and moveInCache updates the ref synchronously so the very next write
  // never reads a snapshot from before React committed the state update.
  const scheduleRef = useRef(schedule);
  useEffect(() => {
    scheduleRef.current = schedule;
  }, [schedule]);

  // Memoised (unlike the portal's plain `|| []`) so CRA's exhaustive-deps
  // rule doesn't flag the activityById useMemo below.
  const activities = useMemo(
    () => schedule.activities || [],
    [schedule.activities]
  );
  const groups = schedule.billing_milestones || [];

  const activityById = useMemo(
    () => new Map(activities.map((a) => [a.id, a])),
    [activities]
  );

  const unmapped = activities.filter((a) => !a.billing_milestone_ids?.length);

  /** Move an activity between the unmapped pool and a milestone, optimistically. */
  const moveInCache = (activityId, toGroupId, fromGroupId) => {
    scheduleRef.current = applyMove(
      scheduleRef.current,
      activityId,
      toGroupId,
      fromGroupId
    );
    setSchedule((old) => applyMove(old, activityId, toGroupId, fromGroupId));
  };

  /**
   * Queue a write for one group. The payload is read from the snapshot at send
   * time, so it always carries the full, current link list — including drops
   * that happened while an earlier write was still in flight.
   */
  const enqueueWrite = (groupId) => {
    setPendingWrites((n) => n + 1);

    queueRef.current = queueRef.current
      .then(async () => {
        const snapshot = scheduleRef.current;
        const group = snapshot?.billing_milestones?.find(
          (g) => g.group_id === groupId
        );
        if (!group) return;

        const ids = group.activity_ids || [];
        const endDates = ids
          .map((id) => snapshot.activities?.find((a) => a.id === id)?.end_date)
          .filter(Boolean);
        const maxEnd = endDates.length
          ? moment.max(endDates.map((d) => moment(d)))
          : null;

        await updatePaymentGroup({
          milestones: ids.map((id) => ({ milestoneId: id })),
          paymentGroup: {
            groupId: group.group_id,
            groupName: group.name,
            refId: group.ref_id ?? snapshot.bid_project?.proposal_id,
            refType: group.ref_type || "proposal",
            type: group.type || "PAYMENT_GROUP",
            dueDate: maxEnd ? maxEnd.format("YYYY-MM-DD") : null,
          },
        });
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message ||
            "Could not save the mapping — reloading."
        );
        // The optimistic state no longer matches the server; pull the truth.
        refetchSchedule();
      })
      .finally(() => setPendingWrites((n) => n - 1));
  };

  const handleDrop = (group) => {
    setHoverGroupId(null);
    const activityId = draggingId;
    setDraggingId(null);
    if (!activityId) return;
    if ((group.activity_ids || []).includes(activityId)) return;

    moveInCache(activityId, group.group_id, null);
    enqueueWrite(group.group_id);
  };

  const handleUnlink = (group, activityId) => {
    moveInCache(activityId, null, group.group_id);
    enqueueWrite(group.group_id);
  };

  // What may take a mapping: Handover can (it means "these activities complete
  // the job"), Initial Deposit and Warranty never can — the deposit is the
  // start of the work and the warranty is a period measured from handover.
  //
  // The type check is done here as well as on the server rather than trusting
  // `group.mappable` alone: an older API response has no such field, and
  // `undefined !== false` would silently make every milestone a drop target.
  const isDropTarget = (group) => {
    if (!editable) return false;
    if (group.mappable === false) return false;
    return (
      !NEVER_MAPPABLE_TYPES.has(String(group.type || "").toUpperCase()) &&
      !NEVER_MAPPABLE_NAMES.some((name) =>
        String(group.name || "").toLowerCase().includes(name)
      )
    );
  };

  // Dates are usually empty at this point in the flow — render the line only
  // when there is something to show instead of a row of dashes.
  const dateLine = (activity) => {
    if (!activity.start_date && !activity.end_date) return null;
    return (
      <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>
        {activity.start_date ? moment(activity.start_date).format("DD MMM") : "—"}
        {" → "}
        {activity.end_date ? moment(activity.end_date).format("DD MMM") : "—"}
      </div>
    );
  };

  const panelHeaderStyle = {
    padding: "12px 16px",
    borderBottom: "1px solid #F3F4F6",
    backgroundColor: "#F9FAFB",
  };
  const panelTitleStyle = {
    fontWeight: 500,
    fontSize: "16px",
    color: "#111827",
    margin: 0,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <StepHeader
        title="Map activities to billing milestones"
        right={
          pendingWrites > 0 ? (
            <span style={{ fontSize: "14px", color: "#6B7280" }}>Saving…</span>
          ) : unmapped.length ? (
            <span style={{ fontSize: "14px", color: "#6B7280" }}>
              {unmapped.length} unmapped
            </span>
          ) : null
        }
      />

      {unmapped.length > 0 && (
        <Notice tone="error">
          Every activity has to belong to a billing milestone before the
          schedule can be saved.
        </Notice>
      )}

      {/* Two columns that collapse to one when the panel gets narrow —
          stands in for the portal's `grid-cols-1 lg:grid-cols-2`. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "24px",
        }}
      >
        {/* -------- unmapped pool -------- */}
        <Panel>
          <div style={panelHeaderStyle}>
            <h3 style={panelTitleStyle}>Unmapped Activities</h3>
          </div>
          {!unmapped.length ? (
            <Empty>No activities found</Empty>
          ) : (
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {unmapped.map((activity) => (
                <li
                  key={activity.id}
                  draggable={editable}
                  onDragStart={() => setDraggingId(activity.id)}
                  onDragEnd={() => setDraggingId(null)}
                  style={{
                    border: `1px solid ${
                      draggingId === activity.id ? "#818CF8" : "#E5E7EB"
                    }`,
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    cursor: editable ? "grab" : "default",
                    opacity:
                      draggingId === activity.id ? 0.4 : editable ? 1 : 0.6,
                  }}
                >
                  <div style={{ color: "#111827" }}>{activity.name}</div>
                  {dateLine(activity)}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* -------- drop targets -------- */}
        <Panel>
          <div style={panelHeaderStyle}>
            <h3 style={panelTitleStyle}>Billing Milestones</h3>
          </div>

          {!groups.length ? (
            <Empty>No billing milestones on this scope.</Empty>
          ) : (
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {groups.map((group) => {
                const droppable = isDropTarget(group);
                const linked = (group.activity_ids || [])
                  .map((id) => activityById.get(id))
                  .filter(Boolean);
                const hovering = hoverGroupId === group.group_id && droppable;

                return (
                  <li
                    key={group.group_id}
                    onDragOver={(e) => {
                      if (!droppable) return;
                      e.preventDefault();
                      if (hoverGroupId !== group.group_id) {
                        setHoverGroupId(group.group_id);
                      }
                    }}
                    onDragLeave={() => setHoverGroupId(null)}
                    onDrop={droppable ? () => handleDrop(group) : undefined}
                    style={{
                      border: `1px solid ${hovering ? "#6366F1" : "#E5E7EB"}`,
                      borderRadius: "12px",
                      padding: "12px",
                      backgroundColor: hovering ? "#EEF2FF" : "#fff",
                      transition: "border-color .15s, background-color .15s",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#0d0d0e",
                        }}
                      >
                        {group.name}
                      </span>
                      {droppable && linked.length > 0 && (
                        <CountPill tone="green">
                          {linked.length} attached
                        </CountPill>
                      )}
                    </div>

                    {linked.length > 0 && (
                      <ul
                        style={{
                          listStyle: "none",
                          margin: 0,
                          padding: 0,
                          marginTop: "8px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {linked.map((activity) => (
                          <li
                            key={activity.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "8px",
                              fontSize: "14px",
                              backgroundColor: "#F9FAFB",
                              borderRadius: "8px",
                              padding: "8px 12px",
                            }}
                          >
                            <span
                              style={{
                                color: "#0d0d0e",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {activity.name}
                            </span>
                            {droppable && (
                              <button
                                type="button"
                                onClick={() => handleUnlink(group, activity.id)}
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  flexShrink: 0,
                                  borderRadius: "50%",
                                  backgroundColor: "#FEF2F2",
                                  color: "#DC2626",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: 0,
                                }}
                                title="Unlink"
                              >
                                <FiX size={14} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {droppable && !linked.length && (
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#9CA3AF",
                          marginTop: "8px",
                          marginBottom: 0,
                          border: "1px dashed #E5E7EB",
                          borderRadius: "8px",
                          padding: "16px 0",
                          textAlign: "center",
                        }}
                      >
                        Drop activities here
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default MappingStep;
